<template>
  <section class="">
    <div class="mx-auto">
      <!-- Sport Filter Toggle -->
      <!-- <div class="flex justify-center mb-24 -mt-20">
        <div class="flex border-b border-gray-300 dark:border-gray-700">
          <button
            v-for="sport in sportOptions"
            :key="sport.value"
            @click="
              selectedSport = sport.value as 'all' | '17841460731525882' | '17841472922905354'
            "
            class="px-6 py-3 text-sm font-medium border-b-4 transition-colors"
            :class="{
              'border-b-gray-400 dark:border-b-gray-200 text-gray-900 dark:text-white':
                selectedSport === sport.value,
              'border-b-gray-300 dark:border-b-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300':
                selectedSport !== sport.value,
            }"
          >
            {{ sport.label }}
          </button>
        </div>
      </div> -->

      <!-- Conditional rendering based on prefersReducedMotion -->
      <template v-if="!prefersReducedMotion">
        <!-- Marquee Container -->
        <div
          id="socials-marquee-wrapper"
          class="relative min-h-[356px] flex items-center justify-center"
        >
          <template v-if="filteredPosts && filteredPosts.length > 0">
            <Vue3Marquee
              :duration="filteredPosts.length * 3.75"
              :clone="true"
              :pauseOnHover="true"
              :pause="false"
            >
              <!-- Instagram Posts -->
              <a
                v-for="(post, index) in filteredPosts"
                :key="`${post.id}-${index}`"
                :href="post.permalink"
                target="_blank"
                rel="noopener noreferrer"
                class="social-card w-[284px] h-[356px] -mr-6 relative cursor-pointer transform-gpu transition-all duration-300 z-10 shadow-[0px_8px_12.8px_11px_rgba(0,0,0,0.29)] relative"
                :class="post.randomRotation"
              >
                <!-- Post Image -->
                <img
                  :src="post.media_storage_url"
                  :alt="post.caption || 'Instagram post'"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
              </a>
            </Vue3Marquee>
          </template>
          <template v-else>
            <div class="text-gray-500 text-lg">No posts available.</div>
          </template>
        </div>
      </template>
      <template v-else>
        <!-- Horizontal scroll container for reduced motion -->
        <div
          class="socials-wrapper relative min-h-[356px] flex items-center justify-center overflow-hidden"
        >
          <template v-if="isLoading">
            <!-- Skeleton Loader -->
            <div class="flex flex-col items-center justify-center space-y-4">
              <div class="text-red-500 text-2xl">Loading...</div>
              <div class="flex space-x-4">
                <div class="bg-red-500 w-[284px] h-[356px] rounded-lg animate-pulse"></div>
                <div
                  class="bg-red-500 w-[284px] h-[356px] rounded-lg hidden sm:block animate-pulse"
                ></div>
                <div
                  class="bg-red-500 w-[284px] h-[356px] rounded-lg hidden md:block animate-pulse"
                ></div>
              </div>
            </div>
          </template>
          <template v-else-if="filteredPosts && filteredPosts.length > 0">
            <div class="socials-scrollable w-full overflow-x-auto py-4">
              <div class="flex items-center gap-lg-4 w-full mx-auto max-w-[1600px]">
                <div class="flex items-center gap-4 px-4">
                  <a
                    v-for="(post, index) in filteredPosts"
                    :key="`${post.id}-${index}`"
                    :href="post.permalink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="social-card flex-shrink-0 w-[284px] h-[356px] relative cursor-pointer transform-gpu transition-all duration-300 z-10 shadow-[0px_8px_12.8px_11px_rgba(0,0,0,0.29)]"
                  >
                    <img
                      :src="post.media_storage_url"
                      :alt="post.caption || 'Instagram post'"
                      class="w-full h-full object-cover"
                    />
                  </a>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="text-gray-500 text-lg">No posts available.</div>
          </template>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Tables } from '~/types/database.types';
import { Vue3Marquee } from 'vue3-marquee';

// Define the type locally since it's not exported from the composable
type InstagramPostWithRotation = Tables<'instagram_media'> & {
  randomRotation: string;
};

const props = defineProps<{
  posts?: InstagramPostWithRotation[];
  isLoading: boolean;
}>();

const prefersReducedMotion = ref(false);

// Sport filter options
const sportOptions = [
  { label: 'All', value: 'all' },
  { label: 'Baseball', value: '17841460731525882' },
  { label: 'Softball', value: '17841472922905354' },
];

const selectedSport = ref<'all' | '17841460731525882' | '17841472922905354'>('all');

// Filter posts based on selected sport
const filteredPosts = computed(() => {
  if (!props.posts) return [];
  if (selectedSport.value === 'all') return props.posts;
  return props.posts.filter((post) => post.account_id === Number(selectedSport.value));
});

onMounted(() => {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  prefersReducedMotion.value = mediaQuery.matches;
  mediaQuery.addEventListener('change', (e) => {
    prefersReducedMotion.value = e.matches;
  });
});
</script>

<style scoped>
#socials-marquee-wrapper .vue3-marquee.horizontal {
  overflow-x: visible !important;
}

.social-card {
  transition: transform 0.1s ease-in-out, z-index 0s step-end;
  outline: 8px solid white;
  outline-offset: -4px; /* Centers the 8px outline */
  border-radius: 10px;
}

/* Custom hover styles for individual cards */
.social-card:hover {
  transform: rotate(0deg) scale(1.05); /* Straighten and scale up */
  z-index: 20; /* Bring to front */
  @media (prefers-reduced-motion) {
    transform: none; /* Disable transform for reduced motion */
  }
}

/* Custom scrollbar for horizontal scroll - hidden by default, shown on hover */
.socials-wrapper ::-webkit-scrollbar {
  height: 4px; /* Always reserve space */
}

.socials-wrapper ::-webkit-scrollbar-track {
  @apply bg-transparent; /* Transparent by default */
  transition: background-color 0.2s ease;
}

.socials-wrapper:hover ::-webkit-scrollbar-track {
  @apply bg-gray-100 dark:bg-gray-800; /* Visible on hover */
}

.socials-wrapper ::-webkit-scrollbar-thumb {
  @apply bg-transparent rounded-full; /* Transparent by default */
  transition: background-color 0.2s ease;
}

.socials-wrapper:hover ::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600; /* Visible on hover */
}

.socials-wrapper ::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-500;
}
</style>
